import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Button } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { toCountryCode } from '../../../../utils/country';
import { fetchPopupAds, loadCoverSize } from './popup-banner.service';
import {
  hasActedOnAd,
  isPopupSnoozed,
  rememberActedOn,
  rememberSnooze,
} from './popup-banner.storage';
import './popup-banner.less';

/** Let the page paint before the popup arrives, so it reads as an offer, not a gate. */
const DEFAULT_DELAY = 1200;

// The dialog follows the artwork's width inside these bounds, so neither ratio nor screen suffers.
const MIN_WIDTH = 360;
const MAX_WIDTH = 760;
// Used when the artwork cannot be measured and the ad runs as text alone.
const TEXT_ONLY_WIDTH = 480;

const clampWidth = (cover) => {
  if (!cover?.width) return TEXT_ONLY_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, cover.width));
};

const PopupBanner = ({ country, delay = DEFAULT_DELAY }) => {
  const [ad, setAd] = useState(null);
  const [cover, setCover] = useState(null);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef();

  const countryId = useSelector((state) => state.auth?.user?.user?.countryId);
  const buyGroupId = useSelector((state) => state.auth?.user?.user?.buygroupId);
  const adCountry = country || toCountryCode(countryId);

  useEffect(() => {
    if (isPopupSnoozed()) return undefined;
    // Ads can be aimed at a country or a buy group, so there is nothing to choose between until
    // the profile has arrived.
    if (!adCountry) return undefined;

    let isCurrent = true;

    const loadAd = async () => {
      const ads = await fetchPopupAds(adCountry, buyGroupId);
      if (!isCurrent) return;

      const next = ads.find((item) => !hasActedOnAd(item.key));
      if (!next) return;

      // A cover that is missing, or never loads, leaves the ad standing on its words.
      const size = next.cover
        ? await loadCoverSize(next.cover).catch(() => null)
        : null;
      if (!isCurrent) return;

      setAd(next);
      setCover(size);
      timerRef.current = setTimeout(() => setVisible(true), delay);
    };

    // Nothing downstream waits on this, so a surprise here stays a silent no-popup.
    loadAd().catch((err) => {
      // eslint-disable-next-line no-console
      console.error('Error preparing popup ad:', err);
    });

    return () => {
      isCurrent = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [adCountry, buyGroupId, delay]);

  /**
   * Following the ad retires it; closing the popup only puts it off. Either way the surface
   * rests for a day, so the calculators page does not hand over the next campaign on the way back.
   */
  const close = useCallback(
    (hasFollowed = false) => {
      if (hasFollowed && ad) rememberActedOn(ad.key);
      else rememberSnooze();
      setVisible(false);
    },
    [ad]
  );

  if (!ad) return null;

  return (
    <Modal
      visible={visible}
      onCancel={() => close()}
      afterClose={() => setAd(null)}
      centered
      width={clampWidth(cover)}
      maskClosable
      destroyOnClose
      wrapClassName="popup-banner-modal"
      title={
        <div className="popup-banner-header">
          <h2 className="popup-banner-title">{ad.title}</h2>
          {ad.description && (
            <p className="popup-banner-meta">{ad.description}</p>
          )}
        </div>
      }
      footer={
        <div className="popup-banner-footer">
          <span className="popup-banner-footer-note">Opens in a new tab</span>
          <div className="popup-banner-footer-actions">
            <Button
              size="large"
              onClick={() => close()}
              className="popup-banner-dismiss"
            >
              Maybe later
            </Button>
            <Button
              type="primary"
              size="large"
              href={ad.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => close(true)}
              className="popup-banner-cta"
            >
              {ad.button_text || 'Find out more'}
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
        </div>
      }
    >
      {cover && (
        <a
          className="popup-banner-cover"
          href={ad.link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => close(true)}
          aria-label={ad.title}
          style={{ aspectRatio: `${cover.width} / ${cover.height}` }}
        >
          {/* Decorative: the heading and the link's own label already carry this in text. */}
          <img src={ad.cover} alt="" />
        </a>
      )}
    </Modal>
  );
};

export default PopupBanner;
